// src/store/slices/userSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  getUsersForAdminThunk,
  approveRejectMultipleUsersThunk,
  blockUnblockMultipleUsersThunk,
  deleteMultipleUsersThunk,
  clearUserError,
  getUserRequestsThunk,
  handleUserRequestThunk,
  deleteUserRequestThunk,
  deleteMultipleUserRequestsThunk,
  createUserByAdminThunk,
  updateUserByAdminThunk,
  getUserAppointmentStatisticsThunk,
  getUserAppointmentInsightsThunk,
  getUserBehaviorAnalysisThunk,
  getAggregateStatisticsThunk,
  getTopCustomersByAppointmentsThunk,
  getHighRiskCustomersThunk,
  getVIPCustomersThunk,
  getBranchPerformanceThunk,
  getUserAnalyticsThunk,
  getUserBranchPreferencesThunk,
  updateUserPrimaryBranchThunk,
  getUserVisitHistoryThunk,
  getUserPurchaseHistoryThunk,
  getUserCustomerProfileThunk,
  updateUserCustomerProfileThunk,
  getUserLoyaltyInfoThunk,
  updateUserLoyaltyPointsThunk,
  getBulkUsersThunk,
  clearUserAnalytics,
  selectUser,
  clearSelectedUser,
  verifyUserKYCThunk,
} from "../thunks/userThunks";

const initialState = {
  // User Management
  users: [],
  selectedUser: null,
  userRequests: [],
  totalRequests: 0,
  totalPages: 1,
  currentPage: 1,
  requestTypes: [],

  // Analytics State
  analytics: {
    // Individual user analytics
    userStatistics: null,
    userInsights: null,
    userBehavior: null,

    // Aggregate analytics
    aggregateStats: null,
    topCustomers: [],
    highRiskCustomers: [],
    vipCustomers: [],
    branchPerformance: [],

    // Combined analytics for dashboard
    userAnalyticsDashboard: null,
  },

  // Branch & Visit Data
  branchData: {
    branchPreferences: null,
    visitHistory: [],
    purchaseHistory: null,
  },

  // Customer Profile
  customerProfile: null,

  // Loyalty
  loyaltyInfo: null,

  // Bulk Operations
  bulkUsers: [],

  // Loading States
  loadingList: false,
  loading: false,
  loadingAnalytics: false,
  loadingBranch: false,
  loadingVisits: false,
  loadingLoyalty: false,

  // Error States
  error: null,
  analyticsError: null,
  branchError: null,

  // Filters
  filters: {
    includeUnverified: false,
    status: null,
    name: null,
    phoneNumber: null,
    email: null,
    gstin: null,
    createdByAdmin: null,
    branchCode: null,
  },
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== ERROR & SELECTION ACTIONS ==========
      .addCase(clearUserError, (state) => {
        state.error = null;
        state.analyticsError = null;
        state.branchError = null;
      })
      .addCase(clearUserAnalytics, (state) => {
        state.analytics = initialState.analytics;
        state.analyticsError = null;
      })
      .addCase(selectUser, (state, action) => {
        state.selectedUser = state.users.find((u) => u._id === action.payload);
      })
      .addCase(clearSelectedUser, (state) => {
        state.selectedUser = null;
        state.analytics.userAnalyticsDashboard = null;
        state.branchData = initialState.branchData;
        state.customerProfile = null;
        state.loyaltyInfo = null;
      })

      // ========== USER MANAGEMENT ==========
      .addCase(getUsersForAdminThunk.pending, (state) => {
        state.loadingList = true;
      })
      .addCase(getUsersForAdminThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.users = action.payload;
      })
      .addCase(getUsersForAdminThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      .addCase(approveRejectMultipleUsersThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveRejectMultipleUsersThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(approveRejectMultipleUsersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(blockUnblockMultipleUsersThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(blockUnblockMultipleUsersThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(blockUnblockMultipleUsersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteMultipleUsersThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteMultipleUsersThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteMultipleUsersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createUserByAdminThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(createUserByAdminThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.users.push(action.payload.user);
      })
      .addCase(createUserByAdminThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateUserByAdminThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserByAdminThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex(
          (user) => user._id === action.payload.user._id
        );
        if (index !== -1) {
          state.users[index] = action.payload.user;
        }
        if (state.selectedUser?._id === action.payload.user._id) {
          state.selectedUser = action.payload.user;
        }
      })
      .addCase(updateUserByAdminThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== USER REQUESTS ==========
      .addCase(getUserRequestsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserRequestsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.userRequests = action.payload.requests;
        state.requestTypes = action.payload.requestTypes;
        state.totalRequests = action.payload.totalRequests;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(getUserRequestsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(handleUserRequestThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(handleUserRequestThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(handleUserRequestThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteUserRequestThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUserRequestThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteUserRequestThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteMultipleUserRequestsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteMultipleUserRequestsThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteMultipleUserRequestsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== APPOINTMENT ANALYTICS ==========
      .addCase(getUserAppointmentStatisticsThunk.pending, (state) => {
        state.loadingAnalytics = true;
        state.analyticsError = null;
      })
      .addCase(getUserAppointmentStatisticsThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.analytics.userStatistics = action.payload.data;
      })
      .addCase(getUserAppointmentStatisticsThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.analyticsError = action.payload;
      })

      .addCase(getUserAppointmentInsightsThunk.pending, (state) => {
        state.loadingAnalytics = true;
      })
      .addCase(getUserAppointmentInsightsThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.analytics.userInsights = action.payload.data;
      })
      .addCase(getUserAppointmentInsightsThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.analyticsError = action.payload;
      })

      .addCase(getUserBehaviorAnalysisThunk.pending, (state) => {
        state.loadingAnalytics = true;
      })
      .addCase(getUserBehaviorAnalysisThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.analytics.userBehavior = action.payload.data;
      })
      .addCase(getUserBehaviorAnalysisThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.analyticsError = action.payload;
      })

      .addCase(getAggregateStatisticsThunk.pending, (state) => {
        state.loadingAnalytics = true;
      })
      .addCase(getAggregateStatisticsThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.analytics.aggregateStats = action.payload;
      })
      .addCase(getAggregateStatisticsThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.analyticsError = action.payload;
      })

      .addCase(getTopCustomersByAppointmentsThunk.pending, (state) => {
        state.loadingAnalytics = true;
      })
      .addCase(
        getTopCustomersByAppointmentsThunk.fulfilled,
        (state, action) => {
          state.loadingAnalytics = false;
          state.analytics.topCustomers = action.payload;
        }
      )
      .addCase(getTopCustomersByAppointmentsThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.analyticsError = action.payload;
      })

      .addCase(getHighRiskCustomersThunk.pending, (state) => {
        state.loadingAnalytics = true;
      })
      .addCase(getHighRiskCustomersThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.analytics.highRiskCustomers = action.payload;
      })
      .addCase(getHighRiskCustomersThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.analyticsError = action.payload;
      })

      .addCase(getVIPCustomersThunk.pending, (state) => {
        state.loadingAnalytics = true;
      })
      .addCase(getVIPCustomersThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.analytics.vipCustomers = action.payload;
      })
      .addCase(getVIPCustomersThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.analyticsError = action.payload;
      })

      .addCase(getBranchPerformanceThunk.pending, (state) => {
        state.loadingAnalytics = true;
      })
      .addCase(getBranchPerformanceThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.analytics.branchPerformance = action.payload;
      })
      .addCase(getBranchPerformanceThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.analyticsError = action.payload;
      })

      // Combined User Analytics Dashboard
      .addCase(getUserAnalyticsThunk.pending, (state) => {
        state.loadingAnalytics = true;
        state.analyticsError = null;
      })
      .addCase(getUserAnalyticsThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.analytics.userAnalyticsDashboard = action.payload;
      })
      .addCase(getUserAnalyticsThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.analyticsError = action.payload;
      })

      // ========== BRANCH MANAGEMENT ==========
      .addCase(getUserBranchPreferencesThunk.pending, (state) => {
        state.loadingBranch = true;
        state.branchError = null;
      })
      .addCase(getUserBranchPreferencesThunk.fulfilled, (state, action) => {
        state.loadingBranch = false;
        state.branchData.branchPreferences = action.payload.data;
      })
      .addCase(getUserBranchPreferencesThunk.rejected, (state, action) => {
        state.loadingBranch = false;
        state.branchError = action.payload;
      })

      .addCase(updateUserPrimaryBranchThunk.pending, (state) => {
        state.loadingBranch = true;
      })
      .addCase(updateUserPrimaryBranchThunk.fulfilled, (state, action) => {
        state.loadingBranch = false;
        state.branchData.branchPreferences = action.payload.data;
      })
      .addCase(updateUserPrimaryBranchThunk.rejected, (state, action) => {
        state.loadingBranch = false;
        state.branchError = action.payload;
      })

      // ========== VISIT & PURCHASE HISTORY ==========
      .addCase(getUserVisitHistoryThunk.pending, (state) => {
        state.loadingVisits = true;
      })
      .addCase(getUserVisitHistoryThunk.fulfilled, (state, action) => {
        state.loadingVisits = false;
        state.branchData.visitHistory = action.payload.data;
      })
      .addCase(getUserVisitHistoryThunk.rejected, (state, action) => {
        state.loadingVisits = false;
        state.error = action.payload;
      })

      .addCase(getUserPurchaseHistoryThunk.pending, (state) => {
        state.loadingVisits = true;
      })
      .addCase(getUserPurchaseHistoryThunk.fulfilled, (state, action) => {
        state.loadingVisits = false;
        state.branchData.purchaseHistory = action.payload.data;
      })
      .addCase(getUserPurchaseHistoryThunk.rejected, (state, action) => {
        state.loadingVisits = false;
        state.error = action.payload;
      })

      .addCase(getUserCustomerProfileThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserCustomerProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.customerProfile = action.payload.profile;
      })
      .addCase(getUserCustomerProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateUserCustomerProfileThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserCustomerProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.customerProfile = action.payload.data;
      })
      .addCase(updateUserCustomerProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== LOYALTY ==========
      .addCase(getUserLoyaltyInfoThunk.pending, (state) => {
        state.loadingLoyalty = true;
      })
      .addCase(getUserLoyaltyInfoThunk.fulfilled, (state, action) => {
        state.loadingLoyalty = false;
        state.loyaltyInfo = action.payload.data;
      })
      .addCase(getUserLoyaltyInfoThunk.rejected, (state, action) => {
        state.loadingLoyalty = false;
        state.error = action.payload;
      })

      .addCase(updateUserLoyaltyPointsThunk.pending, (state) => {
        state.loadingLoyalty = true;
      })
      .addCase(updateUserLoyaltyPointsThunk.fulfilled, (state, action) => {
        state.loadingLoyalty = false;
        state.loyaltyInfo = {
          ...state.loyaltyInfo,
          loyalty: action.payload.loyalty,
        };
      })
      .addCase(updateUserLoyaltyPointsThunk.rejected, (state, action) => {
        state.loadingLoyalty = false;
        state.error = action.payload;
      })

      // ========== BULK OPERATIONS ==========
      .addCase(getBulkUsersThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBulkUsersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.bulkUsers = action.payload;
      })
      .addCase(getBulkUsersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(verifyUserKYCThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyUserKYCThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { userId, verified } = action.payload;

        // Update user in list
        const userIndex = state.users.findIndex((u) => u._id === userId);
        if (userIndex !== -1 && state.users[userIndex].jewelleryProfile) {
          state.users[userIndex].jewelleryProfile.kycVerified = verified;
        }

        // Update selected user
        if (
          state.selectedUser?._id === userId &&
          state.selectedUser.jewelleryProfile
        ) {
          state.selectedUser.jewelleryProfile.kycVerified = verified;
        }
      })
      .addCase(verifyUserKYCThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilters, clearFilters, setCurrentPage } = userSlice.actions;
export default userSlice.reducer;
